import { useState } from 'react';
import { 
  Home, 
  MessageSquare, 
  FileText, 
  Heart, 
  Calendar, 
  DollarSign, 
  Users, 
  Settings, 
  Bell,
  Search,
  Menu,
  X
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Badge } from './ui/badge';
import { AnimatedBadge } from './ui/animated-badge';

interface PortalLayoutProps {
  children: React.ReactNode;
  currentSection: string;
  onSectionChange: (section: string) => void;
}

export function PortalLayout({ children, currentSection, onSectionChange }: PortalLayoutProps) {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [notifications] = useState(3);

  // Mock user data
  const user = {
    name: "Sarah Johnson",
    role: "Senior Sales Associate",
    department: "Retail Operations",
    location: "Chicago, IL",
    employeeId: "AKM-2024-1847",
    avatar: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face"
  };

  const navigationItems = [
    { id: 'dashboard', label: 'Dashboard', icon: Home },
    { id: 'askhr', label: 'AskHR Assistant', icon: MessageSquare, badge: 'New' },
    { id: 'policies', label: 'Policies & Procedures', icon: FileText },
    { id: 'benefits', label: 'Benefits & Wellness', icon: Heart },
    { id: 'timeoff', label: 'Time Off & Scheduling', icon: Calendar },
    { id: 'payroll', label: 'Payroll & Compensation', icon: DollarSign },
    { id: 'directory', label: 'Employee Directory', icon: Users },
    { id: 'settings', label: 'Settings', icon: Settings },
  ];

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  return (
    <div className="h-screen bg-gray-50 dark:bg-gray-900 flex flex-col overflow-hidden">
      {/* Fixed Header */}
      <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-3 flex items-center justify-between flex-shrink-0 z-30">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={toggleSidebar}
            className="md:hidden"
          >
            <Menu className="h-5 w-5" />
          </Button>
          <div className="flex items-center gap-3">
            <div className="bg-blue-600 text-white px-3 py-1 rounded-lg font-semibold">
              AKME
            </div>
            <span className="text-sm text-gray-500">Employee Portal</span>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <div className="hidden md:flex items-center gap-2">
            <Search className="h-4 w-4 text-gray-400" />
            <Input 
              placeholder="Search portal..." 
              className="w-64"
            />
          </div>
          
          <Button variant="ghost" size="sm" className="relative">
            <Bell className="h-5 w-5" />
            {notifications > 0 && (
              <Badge 
                variant="destructive" 
                className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
              >
                {notifications}
              </Badge>
            )}
          </Button>

          <div className="flex items-center gap-3">
            <Avatar className="h-8 w-8">
              <AvatarImage src={user.avatar} alt={user.name} />
              <AvatarFallback>{user.name.split(' ').map(n => n[0]).join('')}</AvatarFallback>
            </Avatar>
            <div className="hidden md:block text-sm">
              <div className="font-medium">{user.name}</div>
              <div className="text-gray-500">{user.role}</div>
            </div>
          </div>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* Fixed Sidebar */}
        <aside className={`
          fixed md:static top-0 bottom-0 left-0 z-50 w-64 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 transform transition-transform duration-300 ease-in-out flex flex-col
          ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
        `}>
          {/* Sidebar Header */}
          <div className="p-4 border-b border-gray-200 dark:border-gray-700 md:hidden">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-gray-900 dark:text-gray-100">Navigation</h2>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={toggleSidebar}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Navigation */}
          <div className="flex-1 overflow-y-auto p-4">
            <div className="hidden md:block mb-6">
              <h2 className="font-semibold text-gray-900 dark:text-gray-100">Navigation</h2>
            </div>

            <nav className="space-y-1">
              {navigationItems.map((item) => {
                const Icon = item.icon;
                const isActive = currentSection === item.id;
                
                return (
                  <Button
                    key={item.id}
                    variant={isActive ? "secondary" : "ghost"}
                    className="w-full justify-start"
                    onClick={() => {
                      onSectionChange(item.id);
                      setIsSidebarOpen(false);
                    }}
                  >
                    <Icon className="h-4 w-4 mr-3" />
                    {item.label}
                    {item.badge && (
                      <AnimatedBadge className="ml-auto">
                        {item.badge}
                      </AnimatedBadge>
                    )}
                  </Button>
                );
              })}
            </nav>
          </div>

          {/* User Info Card - Fixed at bottom */}
          <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700">
            <div className="flex items-center gap-3">
              <Avatar className="h-10 w-10">
                <AvatarImage src={user.avatar} alt={user.name} />
                <AvatarFallback>{user.name.split(' ').map(n => n[0]).join('')}</AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm truncate">{user.name}</div>
                <div className="text-xs text-gray-500 truncate">{user.employeeId}</div>
                <div className="text-xs text-gray-500 truncate">{user.location}</div>
              </div>
            </div>
          </div>
        </aside>

        {/* Sidebar overlay for mobile */}
        {isSidebarOpen && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
            onClick={toggleSidebar}
          />
        )}

        {/* Main Content Area with proper height constraints */}
        <main className="flex-1 flex flex-col min-w-0 overflow-hidden">
          <div className="flex-1 overflow-y-auto p-6">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}